import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Divider,
  InputNumber,
  Modal,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';

import {
  COUNT_AUTO_FREED_PPV_STREAMS,
  GET_PPV_AUTO_FREE_SETTING,
} from 'api/ppvAutoFree/queries';
import {
  REVERT_AUTO_FREED_PPV_STREAMS,
  SET_PPV_AUTO_FREE_SETTING,
} from 'api/ppvAutoFree/mutations';
import { GetPpvAutoFreeSetting } from 'api/ppvAutoFree/types/GetPpvAutoFreeSetting';
import {
  SetPpvAutoFreeSetting,
  SetPpvAutoFreeSettingVariables,
} from 'api/ppvAutoFree/types/SetPpvAutoFreeSetting';
import { CountAutoFreedPpvStreams } from 'api/ppvAutoFree/types/CountAutoFreedPpvStreams';
import { RevertAutoFreedPpvStreams } from 'api/ppvAutoFree/types/RevertAutoFreedPpvStreams';

const { Title, Text, Paragraph } = Typography;

const DEFAULT_HOURS = 48;
const MIN_HOURS = 1;

const PpvAutoFreeSettings: React.FC = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  // Null while the field is empty — coercing a cleared input to 0 would just
  // disable Save with nothing on screen explaining why.
  const [hours, setHours] = useState<number | null>(DEFAULT_HOURS);

  const { data, loading, refetch } = useQuery<GetPpvAutoFreeSetting>(
    GET_PPV_AUTO_FREE_SETTING,
    { fetchPolicy: 'network-only' }
  );

  const { data: countData, refetch: refetchCount } =
    useQuery<CountAutoFreedPpvStreams>(COUNT_AUTO_FREED_PPV_STREAMS, {
      fetchPolicy: 'network-only',
    });

  const [setSetting, { loading: saving }] = useMutation<
    SetPpvAutoFreeSetting,
    SetPpvAutoFreeSettingVariables
  >(SET_PPV_AUTO_FREE_SETTING);

  const [revert, { loading: reverting }] =
    useMutation<RevertAutoFreedPpvStreams>(REVERT_AUTO_FREED_PPV_STREAMS);

  useEffect(() => {
    const setting = data?.getPpvAutoFreeSetting;
    if (!setting) return;

    setIsEnabled(setting.isEnabled);
    setHours(setting.hours);
  }, [data]);

  const autoFreedCount = countData?.countAutoFreedPpvStreams ?? 0;
  const isHoursValid =
    hours !== null && Number.isInteger(hours) && hours >= MIN_HOURS;

  const handleSave = async () => {
    if (!isHoursValid) {
      message.error(`Hours must be a whole number of at least ${MIN_HOURS}.`);
      return;
    }

    try {
      await setSetting({ variables: { input: { isEnabled, hours } } });
      message.success('PPV auto-free settings saved.');
      // Enabling the cron changes how many streams a revert would touch, so the
      // number the Revert section quotes has to be re-read alongside the setting.
      await Promise.all([refetch(), refetchCount()]);
    } catch (error) {
      message.error(
        `Could not save settings${error?.message ? ` (${error.message})` : ''}.`
      );
    }
  };

  const handleRevert = () => {
    Modal.confirm({
      title: 'Restore auto-freed recordings to paid?',
      content: (
        <>
          <Paragraph>
            This puts <Text strong>{autoFreedCount}</Text> PPV recording(s) back
            behind their original price. Recordings that were always free are
            not affected.
          </Paragraph>
          <Paragraph type="secondary">
            If the setting is still enabled, the cron will free them again on
            its next hourly run. Turn it off first.
          </Paragraph>
        </>
      ),
      okText: 'Restore to paid',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const { data: result } = await revert();
          message.success(
            `Restored ${
              result?.revertAutoFreedPpvStreams ?? 0
            } stream(s) to paid.`
          );
          await refetchCount();
        } catch (error) {
          message.error(
            `Could not revert${error?.message ? ` (${error.message})` : ''}.`
          );
        }
      },
    });
  };

  return (
    <Card loading={loading}>
      <Title level={4}>PPV Auto-Free</Title>
      <Paragraph type="secondary">
        Makes a PPV recording free to watch once the configured number of hours
        has passed since the stream&apos;s scheduled date. Runs hourly and only
        touches ended PPV streams. A freed recording also moves into the video
        sitemap so it can be indexed.
      </Paragraph>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space size="middle">
          <Switch checked={isEnabled} onChange={setIsEnabled} />
          <Text>{isEnabled ? 'Enabled' : 'Disabled'}</Text>
        </Space>

        <Space size="middle">
          <Text>Free after</Text>
          <InputNumber
            min={MIN_HOURS}
            precision={0}
            value={hours ?? undefined}
            onChange={(value) =>
              setHours(
                value === null || value === undefined ? null : Number(value)
              )
            }
          />
          <Text>hours</Text>
        </Space>

        {!isHoursValid && (
          <Text type="danger">
            Hours must be a whole number of at least {MIN_HOURS}.
          </Text>
        )}

        {isEnabled && (
          <Alert
            type="warning"
            showIcon
            message="Applies to past streams too"
            description={`Turning this on frees every ended PPV recording older than ${
              isHoursValid ? hours : DEFAULT_HOURS
            } hours, not just future ones. It works through the backlog in batches of 500 per hourly run.`}
          />
        )}

        <Button
          type="primary"
          loading={saving}
          disabled={!isHoursValid}
          onClick={handleSave}
        >
          Save
        </Button>
      </Space>

      <Divider />

      <Title level={5}>Revert</Title>
      <Paragraph type="secondary">
        Auto-freed recordings keep their original price, so they can be put back
        behind the paywall. Currently <Text strong>{autoFreedCount}</Text>{' '}
        recording(s) would be restored.
      </Paragraph>
      <Button
        danger
        loading={reverting}
        disabled={autoFreedCount === 0}
        onClick={handleRevert}
      >
        Restore auto-freed recordings to paid
      </Button>
    </Card>
  );
};

export default PpvAutoFreeSettings;
